import './kp-media-viewer-files.less';

import type {IAugmentedJQuery, IFilterService, IOnChanges, IOnChangesObject, IQService} from 'angular';
import type WalkerService from '../../commons/services/walker.service';
import type {Directory, RawBodyMailSendRequest, ViewableFile} from 'typings/portaro.be.types';
import {SenderDescriptorType} from 'typings/portaro.be.types';
import {ngAsync, ngComponentCssClass} from 'shared/utils/ng-@decorators';
import Selection from 'shared/utils/selection';
import type MailerService from '../../commons/services/mailer.service';
import type {LocalizationService} from '../../commons/providers/localization.provider';
import type {FileService} from '../../file/file.service';
import {getFirstById} from 'shared/utils/array-utils.fp';
import type FinishedResponseInteractionService from '../../commons/services/finished-response-interaction.service';
import Checkbox from '../../commons/ui-components/checkbox/Checkbox.svelte';

export abstract class KpMediaViewerFilesComponentController implements KpMediaViewerFilesComponentBindings, IOnChanges {

    public checkboxComponent = Checkbox;

    public files: ViewableFile[];
    public currentFile: ViewableFile;
    public directory: Directory;
    public editingEnabled: boolean;

    public abstract onSelect(locals: { $file: any });

    public selection: Selection<ViewableFile>;
    public scrollContainer: IAugmentedJQuery;

    /*@ngInject*/
    protected constructor(private $filter: IFilterService,
                          private walker: WalkerService,
                          private fileService: FileService,
                          private $q: IQService,
                          private CONTEXT_PATH: string,
                          private mailerService: MailerService,
                          private finishedResponseInteractionService: FinishedResponseInteractionService,
                          private localizationService: LocalizationService) {
        this.selection = new Selection<ViewableFile>();
    }

    public $onChanges(onChangesObj: IOnChangesObject): void {
        if (onChangesObj.files) {
            this.selection.clear();
        }

        if (onChangesObj.currentFile && onChangesObj.currentFile.currentValue) {
            this.scrollToFile(onChangesObj.currentFile.currentValue);
        }
    }

    public isAllMailable(files: ViewableFile[]): boolean {
        return files.every((file) => file.exportable);
    }

    public isAllPrintable(files: ViewableFile[]): boolean {
        return files.every((file) => file.printable);
    }

    public async mail(files: ViewableFile[]) {
        const request: RawBodyMailSendRequest = {
            senderType: SenderDescriptorType.SYSTEM,
            recipientEmails: null,
            // @kp-localization mediaviewer.mail.Subject
            subject: this.localizationService.get('mediaviewer.mail.Subject'),
            attachments: files,
            // @kp-localization mediaviewer.mail.Body
            body: this.localizationService.get('mediaviewer.mail.Body')
        };
        return this.mailerService.sendMail(request);
    }

    public print(files: ViewableFile[]) {
        const filesParsed = Array.isArray(files) ? files : [files];
        const fileIds = this.$filter('prop')(filesParsed, 'id');
        const fileIdsUrlParam = this.$filter('inline')(fileIds, ',');

        this.walker.newPageOnContext('/export', {target: 'files-print:/html/files-print.vtl', object: fileIdsUrlParam});
    }

    public edit(files: ViewableFile & ViewableFile[]) {
        const filesParsed: ViewableFile[] = Array.isArray(files) ? files : [files];

        if (filesParsed.length > 1) {
            alert('Can edit only one file');
        } else {
            this.fileService.editFileMetadata(files[0]); // TODO after metadata edit (e.g., filename) refresh files list view
        }
    }

    @ngAsync()
    public async remove(filesToDelete: ViewableFile[]) {
        const promises = filesToDelete.map(this.fileService.deleteFile.bind(this.fileService));

        await this.$q.all(promises);

        filesToDelete.forEach((deletedFile) => {
            const indexOfDeleted = this.files.indexOf(deletedFile);

            if (indexOfDeleted > -1) {
                this.files.splice(indexOfDeleted, 1);
            }
        });

        this.selection = new Selection<ViewableFile>();

        // select first if we deleted currently selected one
        if (this.files.length > 0 && !getFirstById(this.currentFile.id)(this.files)) {
            this.onSelect({$file: this.files[0]});
        }
    }

    @ngAsync()
    public async uploadFile(files: File[]) {
        if (!this.directory) {
            throw new Error('No directory to create file');
        }

        const uploadedFiles = await Promise.all(files.map((file) => this.fileService.uploadFile(this.directory.id, file)));

        this.onSelect({$file: uploadedFiles[0]});
    }

    @ngAsync()
    public async createFile(fileName: string) {
        if (!this.directory) {
            throw new Error('No directory to create file');
        }

        let file: ViewableFile;
        try {
            file = await this.fileService.createNewFile(this.directory.id, fileName);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        }

        if (file) {
            await this.onSelect({$file: file});
        }
    }

    public selectFile(file) {
        this.selection.toggle(file);
    }

    private getFileElement(file: ViewableFile): HTMLElement | null {
        if (file) {
            return this.scrollContainer[0].querySelector(`#file-${file.id}`);
        }

        return null;
    }

    private scrollToFile(file: ViewableFile) {
        const elem = this.getFileElement(file);

        if (!elem) {
            return;
        }

        this.scrollContainer[0].scrollTop = elem.offsetTop - 50;
    }
}

/**
 * @ngdoc component
 * @name kpMediaViewerFiles
 * @module portaro.media-viewer
 * @transclude header-left-slot
 *
 * @param {ViewableFile[]} files Files to display.
 * @param {ViewableFile} currentFile Current selected file.
 * @param {Directory} directory Directory of files.
 * @param {boolean} editingEnabled Enables edit mode - uploading / creating files.
 * @param {function} onSelect Function triggered if some file is selected.
 * @param {File} onSelect.$file Selected file.
 *
 * @description
 * Component for selection files in media viewer.
 */
// eslint-disable-next-line max-classes-per-file
export default class KpMediaViewerFilesComponent {
    public static componentName = 'kpMediaViewerFiles';

    @ngComponentCssClass('media-viewer-files')
    public templateUrl = require('./kp-media-viewer-files.tpl.ftl');

    public bindings = {
        files: '<',
        currentFile: '<',
        directory: '<',
        editingEnabled: '<',
        onSelect: '&'
    };

    public controller = KpMediaViewerFilesComponentController;
}

interface KpMediaViewerFilesComponentBindings {
    files: ViewableFile[];
    currentFile: ViewableFile;
    directory: Directory;
    editingEnabled: boolean;

    onSelect(locals: { $file: any });
}
